import React, {memo, useState} from 'react';
import {Node} from "rete-react-render-plugin";
import {getComponents} from "./CustomNodeHelper";
import Title from "./components/Title";
import TextQuestion from "./components/TextQuestion";
import VoiceAnswers from "./components/VoiceAnswers";
import DefaultVoiceAnswer from "./components/DefaultVoiceAnswer";
import TimeLimit from "./components/TimeLimit";
import TextAnswers from "./components/TextAnswers";
import Image from "./components/Image";
import Input from "./components/Input";
import Video from "./components/Video";
import Avatar from "./components/Avatar";
import {NODE_LABEL_DEFAULT_COLOR, SEQUENCE_NODE_WIDTH} from "../stylingConstants";
import SelectLabelDialog from "./components/SelectLabelDialog";
import {NestedNodeType} from "../models/NestedNodeType";
import Divider from "./components/Divider";
import Audio from "./components/Audio";
import TextSlide from "./components/TextSlide";
import {
  NodeContainer,
  HeaderContainer,
  InputContainer,
  NameInput,
  StyledIconButton,
  StyledIcon
} from './SequenceNode.styles';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Infos from "./components/Infos";

// interface CustomNodeProps {
//
// }

export class SequenceNode extends Node {
    render() {
        return <Sequence {...this.props} {...this.state} />
    }
}

const Sequence = memo((props) => {
    const {editor, node, bindSocket, bindControl, outputs, controls, inputs, selected} = props;
    const [isCollapsed, setIsCollapsed] = useState(node.isCollapsed ?? false);
    const [labelColor, setLabelColor] = useState(node.labelColor ?? null);
    const [customName, setCustomName] = useState();
    const [showLabelPopup, setShowLabelPopup] = useState(false);
    const {
        sequenceParentComponent,
        videoComponents,
        avatarComponents,
        textQuestionComponents,
        textAnswerComponents,
        textAnswerTimeLimitComponents,
        voiceAnswerComponents,
        defaultVoiceAnswerComponents,
        voiceAnswerTimeLimitComponents,
        imageComponents,
        textSlideComponents,
        audioComponents,
        infoComponents
    } = getComponents(node, inputs, controls, outputs);
    const customNameOnChange = (newName) => {
        node.customName = newName;
        setCustomName(newName);
    }
    const showLabelPopupOnClick = (editor, node) => {
        setShowLabelPopup(true);
    }
    const hideLabelPopupOnClick = () => {
        setShowLabelPopup(false);
    }
    const onLabelColorChange = (newColor) => {
        node.labelColor = newColor;
        setLabelColor(newColor);
    }
    const onExpandCollapseClick = (editor, node) => {
        const collapse = !isCollapsed;
        node.isCollapsed = collapse;
        setIsCollapsed(collapse);
    }
    const showDivider = (nestedNodeType) => {
        switch (nestedNodeType) {
            case NestedNodeType.VIDEO:
                return videoComponents.controls.length > 0
                    && (textQuestionComponents.controls.length > 0
                    || textAnswerComponents.controls.length > 0
                    || voiceAnswerComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.AVATAR:
                return avatarComponents.controls.length > 0
                    && (textQuestionComponents.controls.length > 0
                    || textAnswerComponents.controls.length > 0
                    || textSlideComponents.controls.length > 0
                    || audioComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.QUESTION:
                return textQuestionComponents.controls.length > 0
                    && (textAnswerComponents.controls.length > 0
                    || voiceAnswerComponents.controls.length > 0
                    || imageComponents.controls.length > 0
                    || audioComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.TEXT_ANSWERS:
                return textAnswerComponents.controls.length > 0
                    && (imageComponents.controls.length > 0
                    || audioComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.VOICE_ANSWERS:
                return voiceAnswerComponents.controls.length > 0
                    && (imageComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.IMAGE:
                return imageComponents.controls.length > 0
                    && (textSlideComponents.controls.length > 0
                    || audioComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.TEXT_SLIDE:
                return textSlideComponents.controls.length > 0
                    && (audioComponents.controls.length > 0
                    || infoComponents.controls.length > 0);
            case NestedNodeType.AUDIO:
                return audioComponents.controls.length > 0
                    && infoComponents.controls.length > 0
            case NestedNodeType.INFOS:
                return false;
            default:
                return false;
        }
    }
    return (
        <NodeContainer selected={selected}>
            <HeaderContainer labelColor={labelColor}>
                {/* Node Input */}
                <Input
                    input={sequenceParentComponent.input}
                    bindSocket={bindSocket}
                />
                <InputContainer>
                    <NameInput
                        labelColor={labelColor}
                        type="text"
                        value={node.customName}
                        placeholder={"Add name..."}
                        onPointerDown={e => e.stopPropagation()}
                        onChange={e => customNameOnChange(e.target.value)}
                    />
                </InputContainer>
                <StyledIconButton onClick={() => showLabelPopupOnClick(editor, node)}>
                    <StyledIcon as={PaletteOutlinedIcon} />
                </StyledIconButton>
                <StyledIconButton onClick={() => onExpandCollapseClick(editor, node)}>
                    {isCollapsed ? (
                        <StyledIcon as={ExpandMoreIcon} />
                    ) : (
                        <StyledIcon as={ExpandLessIcon} />
                    )}
                </StyledIconButton>
            </HeaderContainer>
            {showLabelPopup &&
                <SelectLabelDialog
                    onChange={(newColor) => onLabelColorChange(newColor)}
                    onClose={() => hideLabelPopupOnClick()}
                />
            }
            <div>
                {/* Nested Video */}
                <Title titleControl={videoComponents.titleControl} bindControl={bindControl}/>
                <Video
                    node={node}
                    controls={videoComponents.controls}
                    output={videoComponents.output}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                {showDivider(NestedNodeType.VIDEO) && <Divider height={2}/>}
                {/* Nested Avatar */}
                <Title titleControl={avatarComponents.titleControl} bindControl={bindControl}/>
                <Avatar
                    node={node}
                    controls={avatarComponents.controls}
                    output={avatarComponents.output}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                {showDivider(NestedNodeType.AVATAR) && <Divider height={2}/>}
                {/* Nested Text Question */}
                <Title titleControl={textQuestionComponents.titleControl} bindControl={bindControl}/>
                <TextQuestion controls={textQuestionComponents.controls} bindControl={bindControl} isNodeCollapsed={isCollapsed}/>
                {showDivider(NestedNodeType.QUESTION) && <Divider height={2}/>}
                {/* Nested Text Answers */}
                <Title titleControl={textAnswerComponents.titleControl} bindControl={bindControl}/>
                <TextAnswers
                    controls={textAnswerComponents.controls}
                    orderData={textAnswerComponents.orderData}
                    outputs={textAnswerComponents.outputs}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                <TimeLimit controls={textAnswerTimeLimitComponents.controls} bindControl={bindControl} isNodeCollapsed={isCollapsed}/>
                {showDivider(NestedNodeType.TEXT_ANSWERS) && <Divider height={2}/>}
                {/* Nested Voice Answers */}
                <Title titleControl={voiceAnswerComponents.titleControl} bindControl={bindControl}/>
                <VoiceAnswers
                    controls={voiceAnswerComponents.controls}
                    outputs={voiceAnswerComponents.outputs}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                <DefaultVoiceAnswer
                    controls={defaultVoiceAnswerComponents.controls}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                <TimeLimit controls={voiceAnswerTimeLimitComponents.controls} bindControl={bindControl} isNodeCollapsed={isCollapsed}/>
                {showDivider(NestedNodeType.VOICE_ANSWERS) && <Divider height={2}/>}
                {/* Nested Image */}
                <Title titleControl={imageComponents.titleControl} bindControl={bindControl}/>
                <Image
                    node={node}
                    controls={imageComponents.controls}
                    output={imageComponents.output}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                {showDivider(NestedNodeType.IMAGE) && <Divider height={2}/>}
                {/* Nested Text Slide */}
                <Title titleControl={textSlideComponents.titleControl} bindControl={bindControl}/>
                <TextSlide
                    node={node}
                    controls={textSlideComponents.controls}
                    output={textSlideComponents.output}
                    bindSocket={bindSocket}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                {showDivider(NestedNodeType.TEXT_SLIDE) && <Divider height={2}/>}
                {/* Nested Audio */}
                <Title titleControl={audioComponents.titleControl} bindControl={bindControl}/>
                <Audio controls={audioComponents.controls} bindControl={bindControl} isNodeCollapsed={isCollapsed}/>
                {showDivider(NestedNodeType.AUDIO) && <Divider height={2}/>}
                {/* Infos */}
                <Title titleControl={infoComponents.titleControl} bindControl={bindControl}/>
                <Infos
                    controls={infoComponents.controls}
                    bindControl={bindControl}
                    isNodeCollapsed={isCollapsed}
                />
                {showDivider(NestedNodeType.INFOS) && <Divider height={2}/>}
            </div>
        </NodeContainer>
    );
});